<template>
  <div class="text-left">
    <h3 class="kt-portlet__head-title">Add Quiz/Assessment</h3>
    <v-row id="quiz--wrapper">
      <v-col cols="12">
        <v-row>
          <v-col>
            <v-text-field
              v-model="quiz.title"
              label="Quiz Title"
              placeholder="Type in a Quiz Title"
              :loading="isLoading"
              :disabled="isLoading"
              outlined
            />
          </v-col>
          <v-col>
            <v-text-field
              v-model="quiz.passing_rate"
              label="Passing Rate (%)"
              placeholder="Input passing rate percentage"
              hint="Takers with below passing rate will have the option to retake the quiz"
              persistent-hint
              :loading="isLoading"
              :disabled="isLoading"
              type="number"
              min="0"
              step="0.1"
              outlined
            />
          </v-col>
        </v-row>
      </v-col>
      <v-col>
        <h4 class="kt-portlet__head-title">Questions</h4>
      </v-col>
      <v-col cols="12">
        <v-expansion-panels v-model="openQuestion" focusable accordion>
          <draggable
            :list="quiz.questions"
            @start="dragging = true"
            @end="dragging = false"
            v-model="quiz.questions"
            :element="'div'"
            class="row no-gutters"
            @change="changeOrder"
          >
            <v-expansion-panel v-for="(question, i) in quiz.questions" :key="i">
              <v-expansion-panel-header v-slot="{ open }">
                <v-row no-gutters>
                  <v-col> {{ i + 1 }}.) {{ question.value }} </v-col>
                  <v-col>
                    <v-fade-transition leave-absolute>
                      <span v-if="!open">
                        Type:
                        <strong>{{
                          question.type.toUpperCase().replace('_', ' ')
                            ? question.type.toUpperCase().replace('_', ' ')
                            : 'None Selected'
                        }}</strong>
                      </span>
                    </v-fade-transition>
                  </v-col>
                  <v-col>
                    <v-btn
                      text
                      color="primary"
                      @click.stop="duplicateQuestion(i)"
                      :loading="isLoading"
                      :disabled="isLoading"
                      small
                    >
                      <i class="fal fa-copy mr-1" />
                      Duplicate Question
                    </v-btn>
                    <v-btn
                      text
                      color="error"
                      @click.stop="removeQuestion(i)"
                      :loading="isLoading"
                      :disabled="isLoading"
                      small
                    >
                      <i class="fal fa-trash mr-1" />
                      Delete Question
                    </v-btn>
                  </v-col>
                </v-row>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <div class="questions--wrapper">
                  <v-row>
                    <v-col lg="9" xl="9" md="12" sm="12">
                      <v-text-field
                        outlined
                        label="Question"
                        placeholder="Type in a question"
                        :loading="isLoading"
                        :disabled="isLoading"
                        v-model="question.value"
                      />
                    </v-col>
                    <v-col lg="3" xl="3" sm="12" md="12">
                      <v-select
                        label="Type"
                        placeholder="Select Type"
                        outlined
                        v-model="question.type"
                        :items="questionTypes"
                        item-text="name"
                        item-value="value"
                        @change="changeQuestionType(question.type, i)"
                      />
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col lg="9" xl="9" md="12" sm="12">
                      <v-textarea
                        outlined
                        label="Description"
                        placeholder="Type in Description"
                        hint="Shows and explains the correct answers."
                        persistent-hint
                        :loading="isLoading"
                        :disabled="isLoading"
                        v-model="question.description"
                      />
                    </v-col>
                    <v-col lg="3" xl="3" sm="12" md="12">
                      <v-text-field
                        label="Score value"
                        placeholder="Input Score Value"
                        persistent-hint
                        hint="Passing rate will be based on % of total score of all items"
                        type="number"
                        step="1"
                        min="0"
                        outlined
                        v-model="question.point_value"
                      />
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col>
                      <h4 class="kt-portlet__head-title">
                        Answers to "
                        {{
                          question.value ? question.value : 'Question #' + i + 1
                        }}
                        "
                      </h4>
                    </v-col>
                  </v-row>
                  <template
                    v-if="question.answers.length > 0"
                    class="question--answers--wrapper"
                  >
                    <v-row v-if="question.type !== 'true_false'">
                      <v-col cols="12">
                        <v-expansion-panels focusable accordion>
                          <draggable
                            :list="question.answers"
                            @start="dragging = true"
                            @end="dragging = false"
                            v-model="question.answers"
                            :element="'div'"
                            class="row no-gutters"
                            @change="changeOrder"
                          >
                            <v-expansion-panel
                              v-for="(answer, ai) in question.answers"
                              :key="ai"
                            >
                              <v-expansion-panel-header
                                :disable-icon-rotate="answer.is_correct"
                              >
                                <v-row no-gutters>
                                  <v-col>
                                    {{
                                      answer.value
                                        ? answer.value
                                        : 'Option ' + (ai + 1)
                                    }}
                                  </v-col>
                                  <v-col>
                                    <v-btn
                                      v-if="answer.value"
                                      text
                                      color="primary"
                                      @click.stop="duplicateAnswer(i, ai)"
                                      :loading="isLoading"
                                      :disabled="isLoading"
                                      small
                                    >
                                      <i class="fal fa-copy mr-1" />
                                      Duplicate Answer
                                    </v-btn>
                                    <v-btn
                                      text
                                      color="error"
                                      @click.stop="removeAnswer(i, ai)"
                                      :loading="isLoading"
                                      :disabled="isLoading"
                                      small
                                    >
                                      <i class="fal fa-trash mr-1" />
                                      Delete Answer
                                    </v-btn>
                                  </v-col>
                                </v-row>
                                <template
                                  v-if="answer.is_correct"
                                  v-slot:actions
                                >
                                  <v-icon color="teal"> fal fa-check </v-icon>
                                </template>
                              </v-expansion-panel-header>
                              <v-expansion-panel-content>
                                <v-row dense>
                                  <v-col cols="10">
                                    <v-text-field
                                      ref="question-answers"
                                      :key="ai"
                                      :placeholder="`Option ${ai + 1}`"
                                      :loading="isLoading"
                                      :disabled="isLoading"
                                      v-model="answer.value"
                                      :prepend-icon="showIcon(question.type)"
                                    />
                                  </v-col>
                                  <v-col cols="2">
                                    <v-checkbox
                                      v-if="
                                        answer.value &&
                                        question.type !== 'checklist'
                                      "
                                      :loading="isLoading"
                                      :disabled="isLoading"
                                      v-model="answer.is_correct"
                                      label="This is a correct answer"
                                    ></v-checkbox>
                                  </v-col>
                                </v-row>
                              </v-expansion-panel-content>
                            </v-expansion-panel>
                          </draggable>
                        </v-expansion-panels>
                      </v-col>
                      <v-col
                        v-for="(answer, ai) in question.answers"
                        :key="ai"
                        cols="12"
                      >
                      </v-col>

                      <v-col>
                        <v-btn
                          :loading="isLoading"
                          :disabled="isLoading"
                          @click="addAnswer(i, question.type)"
                          color="primary"
                          small
                        >
                          <i class="fal fa-plus mr-1" />
                          Add Answer
                        </v-btn>
                      </v-col>
                    </v-row>
                    <v-list v-else outlined tile>
                      <v-list-item
                        v-for="(answer, ai) in question.answers"
                        :key="ai"
                      >
                        <v-row>
                          <v-col cols="10">
                            <v-text-field
                              readonly
                              :loading="isLoading"
                              v-model="answer.value"
                            />
                          </v-col>
                          <v-col cols="2">
                            <v-checkbox
                              v-if="answer.value"
                              :loading="isLoading"
                              :disabled="isLoading"
                              v-model="answer.is_correct"
                              @change="trueFalseCorrectAnswer(i, ai)"
                              label="This is a correct answer"
                            ></v-checkbox>
                          </v-col>
                        </v-row>
                      </v-list-item>
                    </v-list>
                  </template>
                </div>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </draggable>
        </v-expansion-panels>
      </v-col>
      <v-col cols="6">
        <v-btn
          :loading="isLoading"
          :disabled="isLoading"
          @click="addQuestion"
          color="primaryMain"
          class="primaryMainText--text"
          small
        >
          <i class="fal fa-plus mr-1" />
          Add Question
        </v-btn>
      </v-col>
      <v-col cols="6" class="text-right">
        <h5>
          Total Score:
          <strong>{{ totalScore }}</strong>
        </h5>
        <h5>
          Passing Score:
          <strong class="green--text">{{ passingScore }}</strong>
        </h5>
        <small> passing rate of {{ quiz.passing_rate }}% </small>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { map as _map, sum as _sum } from 'lodash-es';
import draggable from 'vuedraggable';

export default {
  name: 'CreateQuiz',
  components: {
    draggable,
  },
  props: {
    isLoading: {
      type: Boolean,
      default: false,
    },
    quiz: {
      type: Array,
    },
  },
  data: () => ({
    questionTypes: [
      {
        value: 'multiple_choice',
        name: 'Multiple Choice',
      },
      {
        value: 'true_false',
        name: 'True or False',
      },
      {
        value: 'checklist',
        name: 'Checklist',
      },
    ],
    openQuestion: [0],
    defaultQuestion: {
      isEdit: false,
      value: null,
      type: 'multiple_choice',
      point_value: 1,
      description: null,
      answers: [
        {
          value: null,
          is_correct: false,
        },
      ],
    },
    passing: 70,
    questions: [],
  }),

  computed: {
    totalScore() {
      return _sum(
        _map(this.quiz.questions, (question) => {
          return parseInt(question.point_value);
        })
      );
    },
    passingScore() {
      const passisngRate = this.quiz.passing_rate / 100;
      return Math.ceil(passisngRate * this.totalScore);
    },
  },

  created() {
    //
  },

  methods: {
    changeOrder(e) {
      console.log(e);
    },
    removeQuestion(index) {
      this.quiz.questions.splice(index, 1);
    },
    addQuestion() {
      const newQuestion = {
        value: `Question #${this.quiz.questions.length + 1}`,
        type: 'multiple_choice',
        point_value: 1,
        description: null,
        answers: [
          {
            value: null,
            is_correct: false,
          },
        ],
      };
      this.quiz.questions.push(newQuestion);
      this.openQuestion = [this.quiz.questions.length - 1];
    },
    changeQuestionType(type, index) {
      this.quiz.questions[index].answers = [
        {
          value: null,
          is_correct: false,
        },
      ];

      if (type === 'checklist') {
        this.quiz.questions[index].answers = [
          {
            value: null,
            is_correct: true,
          },
        ];
      }

      if (type === 'true_false') {
        this.quiz.questions[index].answers = [
          {
            value: 'True',
            is_correct: true,
          },
          {
            value: 'False',
            is_correct: false,
          },
        ];
      }
    },
    duplicateQuestion(index) {
      const dupQuestion = this.quiz.questions[index];

      if (dupQuestion.type) {
        const pushQuestion = {
          value: dupQuestion.value,
          type: dupQuestion.type,
          point_value: dupQuestion.point_value,
          description: dupQuestion.description,
          answers: dupQuestion.answers,
        };
        this.quiz.questions.splice(index + 1, 0, pushQuestion);
        this.openQuestion = [index + 1];
      }
    },
    addAnswer(index, type) {
      this.quiz.questions[index].answers.push({
        value: null,
        is_correct: type === 'checklist' ? true : false,
      });
    },
    removeAnswer(questionIndex, answerIndex) {
      this.quiz.questions[questionIndex].answers.splice(answerIndex, 1);
    },
    duplicateAnswer(questionIndex, answerIndex) {
      const dupAnswer = this.quiz.questions[questionIndex].answers[answerIndex];
      const pushAnswer = {
        value: dupAnswer.value,
        is_correct: dupAnswer.is_correct,
      };
      this.quiz.questions[questionIndex].answers.splice(
        answerIndex + 1,
        0,
        pushAnswer
      );
    },
    showIcon(type) {
      if (type === 'multiple_choice') {
        return 'fal fa-circle';
      }

      if (type === 'checklist') {
        return 'fal fa-square';
      }

      return '';
    },
    trueFalseCorrectAnswer(index, answerIndex) {
      this.quiz.questions[index].answers = _map(
        this.quiz.questions[index].answers,
        (answer) => {
          answer.is_correct = false;
          return answer;
        }
      );
      this.quiz.questions[index].answers[answerIndex].is_correct = true;
    },
  },
};
</script>

<style scoped></style>
